import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  ArrowPathIcon,
  CheckBadgeIcon,
  MinusCircleIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import TableComponent from "../../components/TableComponent";
import { toast } from "react-toastify";
import CouponModal from "../../components/admin/CouponModal";
import { formatDate } from "../../utils/helps";
import {
  fetchFeedback,
  updateFeedback,
} from "../../features/admin/FeedbackSlice";
import apiCustom from "../../utils/ApiCustom";

const getUserPermissions = () => {
  const permissions = localStorage.getItem("permissions");
  return permissions
    ? JSON.parse(permissions).map((p) => p.permissionSlug)
    : [];
};

const userId = localStorage.getItem("userId");
const hasPermission = (requiredPermission) => {
  if (userId == 1) return true;
  const userPermissions = getUserPermissions();
  return userPermissions.includes(requiredPermission);
};

const Feedback = () => {
  const dispatch = useDispatch();
  const { posts } = useSelector((state) => state.posts);
  const { feedbacks, status, error } = useSelector((state) => state.feedbacks);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentCoupon, setCurrentCoupon] = useState(null);
  const [filteredFeedbacks, setFilteredFeedbacks] = useState([]);
  const [selectedPostTitle, setSelectedPostTitle] = useState("");

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setCurrentCoupon(null);
  };

  const handleDelete = async (id, status) => {
    const data = { status };
    try {
      await dispatch(updateFeedback({ id, data })).unwrap();
      toast.success("Duyệt góp ý thành công!");
    } catch {
      toast.error("Đã xảy ra lỗi khi duyệt!");
    }
  };

  // Xoá góp ý — dùng apiCustom (không dùng slice)
  const handleRemove = async (id) => {
    try {
      await apiCustom.delete(`comments/${id}`);
      toast.success("Xoá góp ý thành công!");
      dispatch(fetchFeedback()); // reload danh sách
    } catch {
      toast.error("Đã xảy ra lỗi khi xoá!");
    }
  };

  useEffect(() => {
    dispatch(fetchFeedback());
  }, [dispatch]);

  useEffect(() => {
    if (status === "idle") {
      dispatch(fetchFeedback());
    }
  }, [status, dispatch]);

  // Lọc theo tiêu đề bài viết
  useEffect(() => {
    if (!selectedPostTitle) {
      setFilteredFeedbacks(feedbacks);
    } else {
      const filtered = feedbacks.filter((feedback) => {
        const post = posts?.find((post) => post.id === feedback.postId);
        return post?.title === selectedPostTitle;
      });
      setFilteredFeedbacks(filtered);
    }
  }, [feedbacks, posts, selectedPostTitle]);

  // Lấy danh sách tiêu đề duy nhất
  const getUniqueTitles = () => {
    const titles = feedbacks
      ?.map((feedback) => {
        const post = posts?.find((post) => post.id === feedback.postId);
        return post?.title;
      })
      .filter(Boolean);
    return [...new Set(titles)];
  };

  const headers = [
    {
      key: "Tên bài viết",
      label: "Tên bài viết",
      render: (item) => (
        <div>
          <p className="font-medium">
            {posts?.find((e) => e.id === item.postId)?.title}
          </p>
        </div>
      ),
    },
    {
      key: "content",
      label: "Nội dung",
      render: (item) => (
        <div>
          <p>{item.content}</p>
        </div>
      ),
    },
    {
      key: "user",
      label: "Người phản hồi",
      render: (item) => (
        <div>
          <p className="font-medium">{item.user?.userFullName}</p>
        </div>
      ),
    },
    {
      key: "status",
      label: "Trạng thái",
      render: (item) => {
        const s = item.status;
        const isApproved = s === "approved";
        const isRejected = s === "rejected";
        const badgeClass = isApproved
          ? "bg-green-100 text-green-800"
          : isRejected
            ? "bg-red-100 text-red-800"
            : "bg-yellow-100 text-yellow-800"; // Đang chờ (mặc định)
        const label = isApproved
          ? "Đã duyệt"
          : isRejected
            ? "Từ chối"
            : "Đang chờ";
        return (
          <span
            className={`px-3 py-1 rounded-full text-sm whitespace-nowrap ${badgeClass}`}
          >
            {label}
          </span>
        );
      },
    },
    {
      key: "updatedAt",
      label: "Ngày cập nhật",
      render: (item) => (
        <div>
          <p className="font-medium">{formatDate(item.updatedAt)}</p>
        </div>
      ),
    },
  ];

  if (hasPermission("COMMENT_APPROVE") || hasPermission("COMMENT_DELETE")) {
    headers.push({ key: "setting", label: "Thao tác" });
  }

  const tableSettings = {
    renderSettings: (item) => (
      <div className="flex space-x-2">
        {hasPermission("COMMENT_APPROVE") && (
          <button
            onClick={() => handleDelete(item.id, "approved")}
            title="Duyệt"
            className="btn btn-outline btn-accent btn-sm px-3 my-1 rounded"
            disabled={item.status === "approved"}
          >
            <CheckBadgeIcon className="w-5 h-5" />
          </button>
        )}
        {hasPermission("COMMENT_APPROVE") && (
          <button
            onClick={() => handleDelete(item.id, "rejected")}
            title="Từ chối"
            className="btn btn-outline btn-error btn-sm px-3 my-1 rounded"
            disabled={item.status === "rejected"}
          >
            <MinusCircleIcon className="w-5 h-5" />
          </button>
        )}
        {hasPermission("COMMENT_APPROVE") && (
          <button
            onClick={() => handleRemove(item.id)}
            title="Xoá góp ý"
            className="btn btn-outline btn-error btn-sm px-3 my-1 rounded"
          >
            <TrashIcon className="w-5 h-5" />
          </button>
        )}
      </div>
    ),
  };

  if (status === "loading") {
    return (
      <div className="flex justify-center items-center h-64">
        <ArrowPathIcon className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  if (status === "failed") {
    return (
      <div className="p-4 text-red-500 bg-red-50 rounded-md">
        Đã xảy ra lỗi: {error}
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="block md:flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Quản lý góp ý</h1>
        <div className="flex items-center justify-end">
          <div className="relative w-60">
            <div className="flex items-center space-x-2">
              <select
                className="w-full border border-gray-300 rounded-md p-2 pr-8 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                value={selectedPostTitle}
                onChange={(e) => setSelectedPostTitle(e.target.value)}
              >
                <option value="">Tất cả bài viết</option>
                {getUniqueTitles()?.map((title, index) => (
                  <option key={index} value={title}>
                    {title}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      <div className="rounded-lg shadow">
        <TableComponent
          headers={headers}
          data={filteredFeedbacks}
          settings={tableSettings}
        />
      </div>

      {isModalOpen && (
        <CouponModal coupon={currentCoupon} onClose={handleCloseModal} />
      )}
    </div>
  );
};

export default Feedback;
